import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Analytics as DubAnalytics } from "@dub/analytics/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/lib/contexts/AuthContext";
import { SubscriptionProvider } from "@/lib/contexts/SubscriptionContext";
import { FeatureFlagsProvider } from "@/lib/contexts/FeatureFlagsContext";
import { AIUsageProvider } from "@/lib/contexts/AIUsageContext";
import { ThemeProvider } from "@/lib/contexts/ThemeContext";
import { MapProvider } from "@/lib/contexts/MapContext";
import { SiteProvider } from "@/lib/contexts/SiteContext";
import { Toaster } from "@/components/ui/sonner";
import { useIntercom } from "@/lib/hooks/useIntercom";

// New Routing Architecture - Phase 1-3
import { AuthGuard } from "@/components/routing/AuthGuard";
import { ShareGuard } from "@/components/routing/ShareGuard";
import { NotFound } from "@/components/routing/RouteError";
import { PropertyProvider } from "@/lib/contexts/PropertyContext";
import { WorkspaceProvider } from "@/lib/contexts/WorkspaceContext";
import { URLStateProvider } from "@/lib/contexts/URLStateContext";
import { FilterProvider } from "@/lib/contexts/FilterContext";
import { WorkspaceRoute } from "@/components/routing/WorkspaceRoute";
import { CommandSearchProvider } from "@/components/layout/CommandSearch";
import { BrowserSupportBanner } from "@/components/shared/BrowserSupportBanner";

// Pages
import Auth from "@/pages/Auth";
import Verify from "@/pages/Verify";
import Me from "@/pages/Me";
import ChangeEmail from "@/pages/ChangeEmail";
import AcceptInvite from "@/pages/AcceptInvite";
import Home from "@/pages/Home";
// New Dashboard Pages (Phase 4)
import Traffic from "@/pages/dashboard/Traffic";
import VisitorProfile from "@/pages/dashboard/VisitorProfile";
import SplitTestDetail from "@/pages/dashboard/SplitTestDetail";
import SplitTesting from "@/pages/dashboard/SplitTesting";
import CreateExperiment from "@/pages/dashboard/CreateExperiment";
import Heatmaps from "@/pages/dashboard/Heatmaps";
import Funnels from "@/pages/dashboard/Funnels";
import FunnelDetail from "@/pages/dashboard/FunnelDetail";
import FunnelStudio from "@/pages/dashboard/FunnelStudio";
import Export from "@/pages/dashboard/Export";
import Connectors from "@/pages/dashboard/Connectors";
import ConnectorSettings from "@/pages/dashboard/ConnectorSettings";
import Pages from "@/pages/dashboard/Pages";
import ClicksDashboard from "@/pages/dashboard/ClicksDashboard";
import DevicesDashboard from "@/pages/dashboard/DevicesDashboard";
import ChannelsDashboard from "@/pages/dashboard/ChannelsDashboard";
import AnalyticsRedirect from "@/components/dashboard/AnalyticsRedirect";
import AdsAttributionDashboard from "@/pages/dashboard/AdsAttributionDashboard";
import UserSettings from "@/pages/UserSettings";

import SitesDashboard from "@/pages/SitesDashboard";
import WorkspaceHub from "@/pages/WorkspaceHub";
import WorkspaceSettings from "@/pages/workspaces/WorkspaceSettings";
import WorkspaceConnectors from "@/pages/workspaces/WorkspaceConnectorsNew";
import WorkspaceConnectorSettings from "@/pages/workspaces/WorkspaceConnectorSettings";

import WorkspaceOnboardingWizard from "@/pages/workspaces/WorkspaceOnboardingWizardNew";
import ComponentEditor from "@/pages/dashboard/ComponentEditor";
import TestPlanner from "@/pages/dashboard/TestPlanner";
import ApiAccess from "@/pages/dashboard/ApiAccess";
import Resources from "@/pages/dashboard/Resources";
import SiteSettings from "@/pages/dashboard/SiteSettings";

// Dashboard Layouts
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import HubShell from "@/components/dashboard/HubShell";
import ShareLayout from "@/components/share/ShareLayout";

// Tools Pages
import Tools from "@/pages/Tools";
import UTMGenerator from "@/pages/tools/UTMGenerator";
import SampleSizeCalculator from "@/pages/tools/SampleSizeCalculator";
import DurationCalculator from "@/pages/tools/DurationCalculator";
import SignificanceCalculator from "@/pages/tools/SignificanceCalculator";
import ConversionRateCalculator from "@/pages/tools/ConversionRateCalculator";
import Onboarding from "@/pages/Onboarding";
import UnifiedOnboarding from "@/pages/UnifiedOnboarding";
import OnboardingPlan from "@/pages/OnboardingPlan";
import OAuthCallback from "@/pages/OAuthCallback";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import AgentVerify from "@/pages/AgentVerify";
import AgentComplete from "@/pages/AgentComplete";
import TokenHandoff from "@/pages/TokenHandoff";
import McpConnect from "@/pages/McpConnect";
import AgentHome from "@/pages/AgentHome";

// Components
import ProtectedRoute from "@/components/ProtectedRoute";
import { lazy, Suspense } from "react";

const DesignStates = import.meta.env.DEV ? lazy(() => import("@/pages/dev/DesignStates")) : null;
import { usePageTracking } from "@/hooks/usePageTracking";
import { AppPaywallController } from "@/components/shared/AppPaywallController";
import { TrialActivationWatcher } from "@/components/TrialActivationWatcher";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Component to initialize Intercom when user is authenticated
 */
function IntercomInitializer() {
  useIntercom();
  return null;
}

function PageTracker() {
  usePageTracking();
  return null;
}

/**
 * Redirects the retired `/onboarding/suggest-tests` route to the live campaign
 * step, preserving query params (propertyId, domain, goal) so bookmarked or
 * in-flight GenerationBanner CTAs land on the recs screen, not a missing page.
 */
function SuggestTestsRedirect() {
  const location = useLocation();
  return <Navigate to={`/onboarding/campaign${location.search}`} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SubscriptionProvider>
            <AIUsageProvider>
              <FeatureFlagsProvider>
                <ThemeProvider>
                  <Router>
                  <CommandSearchProvider>
                    <IntercomInitializer />
                    <PageTracker />
                    <TrialActivationWatcher />
                    <AppPaywallController />
                    <BrowserSupportBanner />
                    <DubAnalytics
                      publishableKey="dub_pk_nAh18xzwCEyMLHLoHO2lJZtk"
                      domainsConfig={{ refer: "humblytics.dub.link" }}
                      cookieOptions={{ domain: ".humblytics.com" }}
                    />
                    <Routes>
                {/* Public Auth Routes */}
                {/* New unified auth page - primary route */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/token" element={<TokenHandoff />} />
                {/* Legacy routes redirect to unified auth */}
                <Route path="/sign-in" element={<Navigate to="/auth?mode=signin" replace />} />
                <Route path="/sign-up" element={<Navigate to="/auth?mode=signup" replace />} />
                <Route path="/verify" element={<Verify />} />
                <Route path="/me" element={<Me />} />
                <Route path="/change-email" element={<ChangeEmail />} />
                <Route path="/oauth-callback" element={<OAuthCallback />} />
                <Route path="/checkout-success" element={<CheckoutSuccess />} />
                <Route path="/agent-verify" element={<AgentVerify />} />
                <Route path="/agent-complete" element={<AgentComplete />} />
                <Route path="/mcp-connect" element={<McpConnect />} />
                <Route
                  path="/accept-invite/:token"
                  element={<AcceptInvite />}
                />

                {/* Share Routes - Public Access (No Auth Required) */}
                {/* Magic Link format: /s/:magicId/:password */}
                <Route
                  path="/s/:magicId/:password"
                  element={
                    <ShareGuard>
                      <FilterProvider>
                        <URLStateProvider>
                          <MapProvider>
                            <ShareLayout />
                          </MapProvider>
                        </URLStateProvider>
                      </FilterProvider>
                    </ShareGuard>
                  }
                >
                  <Route index element={<Navigate to="traffic" replace />} />
                  <Route path="traffic" element={<Traffic />} />
                  <Route path="pages" element={<Pages />} />
                  <Route path="devices" element={<DevicesDashboard />} />
                  <Route path="locations" element={<AnalyticsRedirect />} />
                  <Route path="channels" element={<ChannelsDashboard />} />
                  <Route path="llm-referrals" element={<AnalyticsRedirect />} />
                  <Route path="clicks" element={<ClicksDashboard />} />
                  <Route path="visitors/:visitorKey" element={<VisitorProfile />} />
                  <Route path="forms" element={<AnalyticsRedirect />} />
                  <Route path="bots" element={<AnalyticsRedirect />} />
                  <Route path="split-testing" element={<SplitTesting />} />
                  <Route
                    path="split-tests/:testSlugId"
                    element={<SplitTestDetail />}
                  />
                  <Route path="heatmaps" element={<Heatmaps />} />
                  <Route path="funnels" element={<Funnels />} />
                  <Route
                    path="funnels/:funnelSlugId"
                    element={<FunnelDetail />}
                  />
                </Route>

                {/* Workspace Routes - New Multi-Source Intelligence Platform */}
                {/* Create new workspace (assign existing sites only) */}
                <Route
                  path="/workspace/new"
                  element={
                    <AuthGuard>
                      <WorkspaceOnboardingWizard mode="addWorkspace" />
                    </AuthGuard>
                  }
                />
                {/* Redirects for old aliases */}
                <Route path="/onboarding/workspace" element={<Navigate to="/workspace/new" replace />} />
                <Route path="/workspace-v2/new" element={<Navigate to="/workspace/new" replace />} />
                <Route path="/add-website/legacy" element={<Navigate to="/add-website" replace />} />

                <Route
                  path="/workspace/:workspaceId"
                  element={
                    <AuthGuard>
                      <WorkspaceProvider>
                        <WorkspaceRoute />
                      </WorkspaceProvider>
                    </AuthGuard>
                  }
                >
                  <Route index element={<Navigate to="traffic" replace />} />
                  <Route path="overview" element={<Navigate to="../traffic" replace relative="path" />} />
                  <Route path="settings" element={<WorkspaceSettings />} />
                  <Route path="traffic" element={<Traffic />} />
                  <Route path="split-testing" element={<SplitTesting />} />
                  <Route
                    path="split-testing/new"
                    element={<CreateExperiment />}
                  />
                  <Route
                    path="split-testing/new/:draftId"
                    element={<CreateExperiment />}
                  />
                  <Route
                    path="split-testing/new/:draftId/:step"
                    element={<CreateExperiment />}
                  />
                  <Route
                    path="experiments/editor/:draftId"
                    element={<ComponentEditor />}
                  />
                  <Route
                    path="split-tests/:testSlugId"
                    element={<SplitTestDetail />}
                  />
                  <Route
                    path="split-tests/:testSlugId/health"
                    element={<Navigate to=".." relative="path" replace />}
                  />
                  <Route path="heatmaps" element={<Heatmaps />} />
                  <Route path="funnels" element={<FunnelStudio />} />
                  <Route path="funnels/studio" element={<FunnelStudio />} />
                  <Route
                    path="funnels/:funnelSlugId"
                    element={<FunnelDetail />}
                  />
                  <Route path="funnel-analyzer" element={<FunnelStudio />} />
                  <Route path="export" element={<Export />} />
                  <Route path="api" element={<ApiAccess />} />
                  <Route path="connectors" element={<WorkspaceConnectors />} />
                  <Route
                    path="connectors/:connectorType"
                    element={<WorkspaceConnectorSettings />}
                  />
                  <Route
                    path="connectors/:connectorType/settings"
                    element={<Navigate to=".." replace relative="path" />}
                  />
                  <Route path="attribution" element={<AdsAttributionDashboard />} />
                  <Route path="ads" element={<Navigate to="../attribution" replace />} />
                  <Route path="pages" element={<Pages />} />
                  <Route path="devices" element={<DevicesDashboard />} />
                  <Route path="locations" element={<AnalyticsRedirect />} />
                  <Route path="channels" element={<ChannelsDashboard />} />
                  <Route path="llm-referrals" element={<AnalyticsRedirect />} />
                  <Route path="clicks" element={<ClicksDashboard />} />
                  <Route path="visitors/:visitorKey" element={<VisitorProfile />} />
                  <Route path="forms" element={<AnalyticsRedirect />} />
                  <Route path="bots" element={<AnalyticsRedirect />} />
                  {/* Insights routes - aliases for pages, clicks, forms */}
                  <Route path="insights/pages" element={<Pages />} />
                  <Route path="insights/clicks" element={<ClicksDashboard />} />
                  <Route path="insights/forms" element={<AnalyticsRedirect />} />
                  {/* Tools Routes (contextual to workspace) */}
                  <Route path="tools" element={<Tools />} />
                  <Route path="tools/utm-generator" element={<UTMGenerator />} />
                  <Route path="tools/significance-calculator" element={<SignificanceCalculator />} />
                  <Route path="tools/conversion-rate-calculator" element={<ConversionRateCalculator />} />
                  <Route path="tools/sample-size-calculator" element={<Navigate to="../test-planner" replace />} />
                  <Route path="tools/duration-calculator" element={<Navigate to="../test-planner" replace />} />
                  <Route path="test-planner" element={<TestPlanner />} />
                  <Route path="resources" element={<Resources />} />
                </Route>

                {/* Property Routes - Legacy Support (Backward Compatibility) */}
                <Route
                  path="/p/:propertySlugId"
                  element={
                    <AuthGuard>
                      <PropertyProvider>
                        <SiteProvider>
                          <FilterProvider>
                            <URLStateProvider>
                              <MapProvider>
                                <DashboardLayout />
                              </MapProvider>
                            </URLStateProvider>
                          </FilterProvider>
                        </SiteProvider>
                      </PropertyProvider>
                    </AuthGuard>
                  }
                >
                  <Route index element={<Navigate to="traffic" replace />} />
                  <Route path="traffic" element={<Traffic />} />
                  <Route path="split-testing" element={<SplitTesting />} />
                  <Route
                    path="split-testing/new"
                    element={<CreateExperiment />}
                  />
                  <Route
                    path="split-testing/new/:draftId"
                    element={<CreateExperiment />}
                  />
                  <Route
                    path="split-testing/new/:draftId/:step"
                    element={<CreateExperiment />}
                  />
                  <Route
                    path="experiments/editor/:draftId"
                    element={<ComponentEditor />}
                  />
                  <Route
                    path="split-tests/:testSlugId"
                    element={<SplitTestDetail />}
                  />
                  <Route
                    path="split-tests/:testSlugId/health"
                    element={<Navigate to=".." relative="path" replace />}
                  />
                  <Route path="heatmaps" element={<Heatmaps />} />
                  <Route path="funnels" element={<FunnelStudio />} />
                  <Route path="funnels/studio" element={<FunnelStudio />} />
                  <Route
                    path="funnels/:funnelSlugId"
                    element={<FunnelDetail />}
                  />
                  <Route path="funnel-analyzer" element={<FunnelStudio />} />
                  <Route path="export" element={<Export />} />
                  <Route path="api" element={<ApiAccess />} />
                  <Route path="connectors" element={<Connectors />} />
                  <Route
                    path="connectors/:connectorType"
                    element={<ConnectorSettings />}
                  />
                  <Route
                    path="connectors/:connectorType/settings"
                    element={<Navigate to=".." replace relative="path" />}
                  />
                  <Route path="attribution" element={<AdsAttributionDashboard />} />
                  <Route path="ads" element={<Navigate to="../attribution" replace />} />
                  <Route path="pages" element={<Pages />} />
                  <Route path="devices" element={<DevicesDashboard />} />
                  <Route path="locations" element={<AnalyticsRedirect />} />
                  <Route path="channels" element={<ChannelsDashboard />} />
                  <Route path="llm-referrals" element={<AnalyticsRedirect />} />
                  <Route path="clicks" element={<ClicksDashboard />} />
                  <Route path="visitors/:visitorKey" element={<VisitorProfile />} />
                  <Route path="forms" element={<AnalyticsRedirect />} />
                  <Route path="bots" element={<AnalyticsRedirect />} />
                  {/* Insights routes - aliases for pages, clicks, forms */}
                  <Route path="insights/pages" element={<Pages />} />
                  <Route path="insights/clicks" element={<ClicksDashboard />} />
                  <Route path="insights/forms" element={<AnalyticsRedirect />} />
                  {/* Tools Routes (contextual to property) */}
                  <Route path="tools" element={<Tools />} />
                  <Route path="tools/utm-generator" element={<UTMGenerator />} />
                  <Route path="tools/significance-calculator" element={<SignificanceCalculator />} />
                  <Route path="tools/conversion-rate-calculator" element={<ConversionRateCalculator />} />
                  <Route path="tools/sample-size-calculator" element={<Navigate to="../test-planner" replace />} />
                  <Route path="tools/duration-calculator" element={<Navigate to="../test-planner" replace />} />
                  <Route path="test-planner" element={<TestPlanner />} />
                  <Route path="resources" element={<Resources />} />
                  <Route path="settings" element={<SiteSettings />} />
                </Route>

                {/* Dashboard - Main Workspace Hub. Nested inside HubShell so the
                    sidebar (workspace switcher, nav, user dropdown, theme toggle)
                    persists on the landing page. HubShell is a minimal layout —
                    no property/workspace data fetching, no paywall/trial banners,
                    no AI sidebar. Use DashboardLayout for routes that need those. */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<HubShell />}>
                    <Route path="/dashboard" element={<WorkspaceHub />} />
                  </Route>
                  <Route path="/workspaces" element={<SitesDashboard />} />
                </Route>

                {/* Other Protected Routes (Settings, Tools) */}
                <Route element={<ProtectedRoute />}>
                  {/* Primary add-website flow now uses the wizard */}
                  <Route
                    path="/add-website"
                    element={<WorkspaceOnboardingWizard mode="addSite" />}
                  />
                  <Route path="/user-settings" element={<UserSettings />} />
                  <Route path="/agent" element={<AgentHome />} />
                  <Route path="/onboarding" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/start" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/plan" element={<OnboardingPlan />} />
                  <Route path="/onboarding/profile" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/connect-agent" element={<Navigate to="/agent" replace />} />
                  <Route path="/onboarding/select-goal" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/add-site" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/install-script" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/research" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/campaign" element={<UnifiedOnboarding />} />
                  <Route path="/onboarding/suggest-tests" element={<SuggestTestsRedirect />} />
                  {/* Tools Routes */}
                  <Route path="/tools" element={<Tools />} />
                  <Route
                    path="/tools/utm-generator"
                    element={<UTMGenerator />}
                  />
                  <Route
                    path="/tools/significance-calculator"
                    element={<SignificanceCalculator />}
                  />
                  <Route
                    path="/tools/conversion-rate-calculator"
                    element={<ConversionRateCalculator />}
                  />
                  <Route
                    path="/tools/sample-size-calculator"
                    element={<Navigate to="/tools/test-planner" replace />}
                  />
                  <Route
                    path="/tools/duration-calculator"
                    element={<Navigate to="/tools/test-planner" replace />}
                  />
                  <Route
                    path="/tools/test-planner"
                    element={<TestPlanner />}
                  />
                </Route>

                {/* Default redirect */}
                <Route path="/" element={<Home />} />

                {/* DEV-only: every designed state rendered with mock data */}
                {DesignStates && (
                  <Route
                    path="/dev/states"
                    element={<Suspense fallback={null}><DesignStates /></Suspense>}
                  />
                )}

                {/* 404 Not Found */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <Toaster />
              </CommandSearchProvider>
              </Router>
              </ThemeProvider>
            </FeatureFlagsProvider>
            </AIUsageProvider>
          </SubscriptionProvider>
        </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
